import { useState } from "react";

import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { Stack } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { P2bbRenderer } from "@src/features/mobileAnalytics/statSigRenderers/P2bbRenderer";
import { P2bcRenderer } from "@src/features/mobileAnalytics/statSigRenderers/P2bcRenderer";
import { TabType } from "@src/features/mobileAnalytics/statSigRenderers/types";
import { UpliftRenderer } from "@src/features/mobileAnalytics/statSigRenderers/UpliftRenderer";
import { ValueRenderer } from "@src/features/mobileAnalytics/statSigRenderers/ValueRenderer";
import { UseQueryResult } from "@tanstack/react-query";

import { TabbedNavigation } from "../../shared/components/TabbedNavigation";
import { useVariantData } from "../../shared/hooks/useVariantData";

interface MobileStatSigSectionProps {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  selectedMetricId: string;
  graphConfigs: GraphConfig[];
  controlGroupName?: string;
  selectedConfig: GraphConfig;
}

export const MobileStatSigSection = ({
  variationOverviewData,
  selectedMetricId,
  graphConfigs,
  controlGroupName = "Default",
  selectedConfig,
}: MobileStatSigSectionProps) => {
  const [activeTab, setActiveTab] = useState<TabType>("p2bb");
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const isLoading =
    variationOverviewData[0]?.isLoading || variationOverviewData[0]?.isPending;

  const { variantData, selectedBaselineId } = useVariantData({
    variationOverviewData,
    selectedMetricId,
    selectedConfig,
  });

  const foundConfig = graphConfigs.find((config) =>
    config.impactMetric
      ? config.impactMetric === selectedMetricId
      : config.metrics[0] === selectedMetricId
  );
  const hasProbabilities = foundConfig?.hasProbability || false;

  if (!hasProbabilities) {
    return null;
  }

  const currency = storeCurrencyConfig?.value || {
    code: "USD",
    locale: "en-US",
  };

  const tabLabels = [
    "Probability to be best",
    `Probability to beat ${controlGroupName}`,
    "Uplift",
    "Value",
  ];

  const handleTabChange = (tabName: string) => {
    if (tabName === tabLabels[0]) {
      setActiveTab("p2bb");
    } else if (tabName === tabLabels[1]) {
      setActiveTab("p2bc");
    } else if (tabName === tabLabels[2]) {
      setActiveTab("uplift");
    } else {
      setActiveTab("value");
    }
  };

  const renderActiveTab = () => {
    const commonProps = {
      variantData,
      selectedBaselineId,
      isLoading,
      currency,
    };

    switch (activeTab) {
      case "p2bb":
        return <P2bbRenderer {...commonProps} />;
      case "p2bc":
        return <P2bcRenderer {...commonProps} />;
      case "uplift":
        return (
          <UpliftRenderer {...commonProps} selectedConfig={selectedConfig} />
        );
      case "value":
        return (
          <ValueRenderer {...commonProps} selectedConfig={selectedConfig} />
        );
    }
  };

  return (
    <Stack spacing={2}>
      {/* Tab Navigation */}
      <TabbedNavigation
        tabs={tabLabels}
        onTabChange={handleTabChange}
        defaultTab={tabLabels[0]}
        buttonSize="extraSmall"
        buttonSx={{ fontSize: 12 }}
      />

      {/* Render active tab content */}
      {renderActiveTab()}
    </Stack>
  );
};
